import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Table, Header, Modal, Form, Message, Icon } from 'semantic-ui-react';
import AdminLayout from '../../components/layout/AdminLayout';
import { users, roles } from '../../services/api';

function Users() {
  const queryClient = useQueryClient();
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    roles: []
  });

  const { data: usersList, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users'],
    queryFn: () => users.list(),
  });

  const { data: rolesList, isLoading: isLoadingRoles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => roles.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => users.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setCreateModalOpen(false);
      setError('');
      setFormData({ name: '', email: '', password: '', roles: [] });
    },
    onError: (err) => setError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (data) => users.update(selectedUser.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setEditModalOpen(false);
      setError('');
      setSelectedUser(null);
    },
    onError: (err) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => users.delete(id),
    onSuccess: () => queryClient.invalidateQueries(['users']),
    onError: (err) => setError(err.message),
  });

  const handleInputChange = (e, { name, value }) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreate = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    const updateData = {
      ...formData,
      password: formData.password || undefined // Only send password if it's not empty
    };
    updateMutation.mutate(updateData);
  };

  const handleDelete = (user) => {
    if (window.confirm(`Are you sure you want to delete ${user.name}?`)) {
      deleteMutation.mutate(user.id);
    }
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      roles: user.roles.map(role => role.id)
    });
    setEditModalOpen(true);
  };

  const roleOptions = rolesList?.data?.map(role => ({
    key: role.id,
    text: role.name,
    value: role.id
  })) || [];

  if (isLoadingUsers || isLoadingRoles) {
    return <div>Loading...</div>;
  }

  return (
    <AdminLayout>
      <Helmet>
        <title>Miakara Soap Recipes - User Management</title>
      </Helmet>

      <Header as="h1">User Management</Header>
      <Button 
        primary 
        onClick={() => {
          setFormData({ name: '', email: '', password: '', roles: [] });
          setCreateModalOpen(true);
        }} 
        style={{ marginBottom: 16 }}
      >
        Create User
      </Button>

      {error && <Message negative content={error} />}

      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Name</Table.HeaderCell>
            <Table.HeaderCell>Email</Table.HeaderCell>
            <Table.HeaderCell>Roles</Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {usersList?.data?.data?.map(user => (
            <Table.Row key={user.id}>
              <Table.Cell>{user.name}</Table.Cell>
              <Table.Cell>{user.email}</Table.Cell>
              <Table.Cell>
                {user.roles?.map(role => role.name).join(', ')}
              </Table.Cell>
              <Table.Cell>
                <Button.Group size="tiny">
                  <Button
                    icon
                    onClick={() => handleEditClick(user)}
                  >
                    <Icon name="edit" />
                  </Button>
                  <Button icon negative onClick={() => handleDelete(user)}>
                    <Icon name="trash" />
                  </Button>
                </Button.Group>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {/* Create Modal */}
      <Modal
        open={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
      >
        <Modal.Header>Create User</Modal.Header>
        <Modal.Content>
          <Form onSubmit={handleCreate}>
            <Form.Input
              required
              label="Name"
              name="name"
              placeholder="Enter name"
              value={formData.name}
              onChange={handleInputChange}
            />
            <Form.Input
              required
              type="email"
              label="Email"
              name="email"
              placeholder="Enter email"
              value={formData.email}
              onChange={handleInputChange}
            />
            <Form.Input
              required
              type="password"
              label="Password"
              name="password"
              placeholder="Enter password"
              value={formData.password}
              onChange={handleInputChange}
            />
            <Form.Dropdown
              label="Roles"
              name="roles"
              placeholder="Select roles"
              fluid
              multiple
              selection
              options={roleOptions}
              value={formData.roles}
              onChange={handleInputChange}
            />
            <Button type="submit" primary>Create</Button>
          </Form>
        </Modal.Content>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
      >
        <Modal.Header>Edit User</Modal.Header>
        <Modal.Content>
          <Form onSubmit={handleUpdate}>
            <Form.Input
              required
              label="Name"
              name="name"
              placeholder="Enter name"
              value={formData.name}
              onChange={handleInputChange}
            />
            <Form.Input
              required
              type="email"
              label="Email"
              name="email"
              placeholder="Enter email"
              value={formData.email}
              onChange={handleInputChange}
            />
            <Form.Input
              type="password"
              label="Password"
              name="password"
              placeholder="Enter new password (leave blank to keep current)"
              value={formData.password}
              onChange={handleInputChange}
            />
            <Form.Dropdown
              label="Roles"
              name="roles"
              placeholder="Select roles"
              fluid
              multiple
              selection
              options={roleOptions}
              value={formData.roles}
              onChange={handleInputChange}
            />
            <Button type="submit" primary>Update</Button>
          </Form>
        </Modal.Content>
      </Modal>
    </AdminLayout>
  );
}

export default Users; 